import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Upload, Download, UserPlus, Check } from 'lucide-react';
import { Modal } from '@shared/components/ui/Modal';
import { candidateService } from '@core/supabase/services/candidateService';
import { applicationService } from '@core/supabase/services/applicationService';
import { jobService } from '@core/supabase/services/jobService';
import {
  CANDIDATE_INTAKE_CHANNELS,
  CANDIDATE_IMPORT_COLUMN_HINTS,
  CANDIDATE_LIFECYCLE_HELPER,
  CANDIDATE_LIFECYCLE_PHASES,
  CANDIDATE_WIZARD_HIDE_KEY,
} from '@core/constants/candidateLifecycle';
import type { CandidateImportRow } from '@core/candidates/parseCandidateSpreadsheet';
import { candidateImportCsvTemplate, parseCandidateSpreadsheet } from '@core/candidates/parseCandidateSpreadsheet';
import type { Job, CandidateSource } from '@core/types';

export type CandidateWizardPath = 'balanced' | 'import_first' | 'source_first';

type StepKind = 'decision' | 'lifecycle' | 'channel' | 'method' | 'intake' | 'job_fit' | 'review';

const STEPS: StepKind[] = ['decision', 'lifecycle', 'channel', 'method', 'intake', 'job_fit', 'review'];

const STEP_LABELS: Record<StepKind, string> = {
  decision: 'Choose path',
  lifecycle: 'Lifecycle overview',
  channel: 'Source channel',
  method: 'Entry method',
  intake: 'Candidate data',
  job_fit: 'Job & fitment',
  review: 'Review & save',
};

/** Horizontal step tracker — circles + connecting lines + labels */
function HorizontalStepper({
  steps,
  currentIdx,
}: {
  steps: { id: string; label: string }[];
  currentIdx: number;
}) {
  return (
    <div className="w-full select-none" aria-label="Progress">
      <div className="flex items-start">
        {steps.map((s, i) => {
          const done    = i < currentIdx;
          const active  = i === currentIdx;
          const isFirst = i === 0;
          const isLast  = i === steps.length - 1;

          const circleBg  = done
            ? 'bg-emerald-500 border-emerald-500'
            : active
            ? 'bg-[var(--vscode-primary)] border-[var(--vscode-primary)]'
            : 'bg-[var(--vscode-bg-secondary)] border-[var(--vscode-border-primary)]';

          const lineColor = (idx: number) =>
            idx < currentIdx
              ? 'bg-emerald-500'
              : 'bg-[var(--vscode-border-primary)]';

          return (
            <div key={s.id} className="flex flex-col items-center flex-1 min-w-0">
              {/* row: left-line · circle · right-line */}
              <div className="flex items-center w-full">
                {/* left connector */}
                <div className={`flex-1 h-0.5 transition-colors ${isFirst ? 'invisible' : lineColor(i - 1)}`} />

                {/* circle */}
                <div
                  className={`flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${circleBg} ${active ? 'ring-4 ring-[var(--vscode-primary)]/20' : ''}`}
                >
                  {done ? (
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  ) : active ? (
                    <span className="w-2.5 h-2.5 rounded-full bg-white" />
                  ) : (
                    <span className={`text-[10px] font-bold ${active ? 'text-white' : 'vscode-text-tertiary'}`}>
                      {i + 1}
                    </span>
                  )}
                </div>

                {/* right connector */}
                <div className={`flex-1 h-0.5 transition-colors ${isLast ? 'invisible' : lineColor(i)}`} />
              </div>

              {/* label */}
              <p
                className={`mt-1.5 text-center leading-tight px-0.5 truncate w-full text-[10px] font-medium transition-colors ${
                  active ? 'text-[var(--vscode-primary)] font-semibold'
                  : done  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'vscode-text-tertiary'
                }`}
                title={s.label}
              >
                {s.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Shared option card — vertical, full-width */
function OptionCard({
  selected,
  onClick,
  title,
  desc,
  badge,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  desc?: string;
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-lg border transition-colors ${
        selected
          ? 'border-[var(--vscode-primary)] bg-[var(--brand-accent-bg,rgba(139,128,249,0.08))] ring-1 ring-[var(--vscode-primary)]/30'
          : 'border-[var(--vscode-border-primary)] hover:border-[var(--vscode-primary)]/50 bg-[var(--vscode-bg-secondary)]'
      }`}
    >
      {/* radio dot */}
      <span
        className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
          selected
            ? 'border-[var(--vscode-primary)] bg-[var(--vscode-primary)]'
            : 'border-[var(--vscode-border-primary)]'
        }`}
      >
        {selected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold vscode-text-primary">
          {title}
          {badge && (
            <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--vscode-primary)]/15 text-[var(--vscode-primary)]">
              {badge}
            </span>
          )}
        </span>
        {desc && <span className="block text-xs vscode-text-secondary mt-0.5 leading-relaxed">{desc}</span>}
      </span>
    </button>
  );
}

export function readCandidateWizardHidden(): boolean {
  if (typeof window === 'undefined') return false;
  try { return localStorage.getItem(CANDIDATE_WIZARD_HIDE_KEY) === '1'; } catch { return false; }
}

export function CandidateIntakeWizardModal({
  open, onDismiss, onSkipClassic, onDone, orgId, userId,
}: {
  open: boolean;
  onDismiss: () => void;
  onSkipClassic: () => void;
  onDone: (result: { candidateIds: string[]; jobId: string | null }) => void;
  orgId: string;
  userId: string;
}) {
  const [stepIdx, setStepIdx] = useState(0);
  const [path, setPath] = useState<CandidateWizardPath>('balanced');
  const [hideFuture, setHideFuture] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [intakeId, setIntakeId] = useState(CANDIDATE_INTAKE_CHANNELS[0]?.id ?? 'linkedin');
  const [method, setMethod] = useState<'manual' | 'bulk'>('manual');
  const [manual, setManual] = useState({
    full_name: '', email: '', phone: '', skills: '',
    current_company: '', current_title: '', location: '', linkedin_url: '',
  });
  const [bulkRows, setBulkRows] = useState<CandidateImportRow[]>([]);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobId, setJobId] = useState('');
  const [createApplication, setCreateApplication] = useState(true);
  const [computeFitment, setComputeFitment] = useState(true);

  useEffect(() => {
    if (!open || !orgId) return;
    void jobService.list(orgId).then((r) => {
      const list = Array.isArray(r) ? r : (r as { data: Job[] }).data;
      setJobs(list);
    });
  }, [open, orgId]);

  useEffect(() => { if (!open) return; setStepIdx(0); setErr(''); }, [open]);

  const intake = CANDIDATE_INTAKE_CHANNELS.find((c) => c.id === intakeId) ?? CANDIDATE_INTAKE_CHANNELS[0];
  const step = STEPS[stepIdx] ?? 'decision';
  const total = STEPS.length;

  const persistHide = () => {
    if (!hideFuture) return;
    try { localStorage.setItem(CANDIDATE_WIZARD_HIDE_KEY, '1'); } catch { /* ignore */ }
  };

  const candidateSourceForImport = (): CandidateSource =>
    method === 'bulk' ? 'bulk_upload' : intake.source;

  const buildPayloadFromManual = () => ({
    org_id: orgId, created_by: userId,
    full_name: manual.full_name.trim(), email: manual.email.trim(),
    phone: manual.phone.trim() || null, linkedin_url: manual.linkedin_url.trim() || null,
    source_channel: intake.channelLabel, source: candidateSourceForImport(),
    utm_source: null, utm_medium: null, utm_campaign: null,
    current_company: manual.current_company.trim() || null,
    current_title: manual.current_title.trim() || null,
    location: manual.location.trim() || null,
    avatar_url: null,
    base_resume_url: null,
    parsed_skills: manual.skills ? manual.skills.split(/[,;/|]/).map((s) => s.trim()).filter(Boolean) : null,
    years_exp: null, education: null, tags: [], notes: null, recruiter_rating: null, is_active: true,
  });

  const buildPayloadFromRow = (row: CandidateImportRow) => ({
    org_id: orgId, created_by: userId,
    full_name: row.full_name.trim(), email: row.email.trim(),
    phone: row.phone, linkedin_url: row.linkedin_url,
    source_channel: intake.channelLabel, source: 'bulk_upload' as CandidateSource,
    utm_source: null, utm_medium: null, utm_campaign: null,
    current_company: row.current_company, current_title: row.current_title, location: row.location,
    avatar_url: null,
    base_resume_url: null, parsed_skills: row.skills.length ? row.skills : null,
    years_exp: null, education: null, tags: [], notes: null, recruiter_rating: null, is_active: true,
  });

  const canNext = (): boolean => {
    setErr('');
    if (step === 'intake') {
      if (method === 'manual') {
        if (!manual.full_name.trim() || !manual.email.trim()) { setErr('Name and email are required.'); return false; }
      } else if (bulkRows.length === 0) {
        setErr('Upload a CSV or Excel file with at least one valid row.'); return false;
      }
    }
    if (step === 'job_fit' && createApplication && !jobId) {
      setErr('Pick a job to attach, or turn off "Create application".'); return false;
    }
    return true;
  };

  const goNext = () => { if (!canNext()) return; if (stepIdx < total - 1) setStepIdx((i) => i + 1); };
  const goBack = () => { setErr(''); if (stepIdx > 0) setStepIdx((i) => i - 1); else onDismiss(); };
  const skip = () => { persistHide(); onSkipClassic(); };

  const onFile = async (f: File | null) => {
    if (!f) return;
    setErr(''); setParseWarnings([]);
    try {
      const { rows, parseErrors } = await parseCandidateSpreadsheet(f);
      setBulkRows(rows); setParseWarnings(parseErrors);
      if (rows.length === 0) setErr('No importable rows found.');
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Failed to parse file'); setBulkRows([]);
    }
  };

  const runSubmit = async () => {
    setLoading(true); setErr('');
    const createdIds: string[] = [];
    try {
      const selectedJob = jobId ? jobs.find((j) => j.id === jobId) ?? null : null;
      if (method === 'manual') {
        const c = await candidateService.create(buildPayloadFromManual() as Parameters<typeof candidateService.create>[0]);
        createdIds.push(c.id);
        if (createApplication && selectedJob) {
          const skills = c.parsed_skills ?? [];
          if (computeFitment) {
            await applicationService.upsertApplicationWithSkillFitment({ orgId, userId, candidateId: c.id, job: selectedJob, candidateSkillList: skills });
          } else {
            await applicationService.create({ org_id: orgId, candidate_id: c.id, job_id: selectedJob.id, client_id: selectedJob.client_id, created_by: userId });
          }
        }
      } else {
        for (const row of bulkRows) {
          try {
            const c = await candidateService.create(buildPayloadFromRow(row) as Parameters<typeof candidateService.create>[0]);
            createdIds.push(c.id);
            if (createApplication && selectedJob) {
              const skills = c.parsed_skills ?? row.skills ?? [];
              if (computeFitment) {
                await applicationService.upsertApplicationWithSkillFitment({ orgId, userId, candidateId: c.id, job: selectedJob, candidateSkillList: skills });
              } else {
                await applicationService.create({ org_id: orgId, candidate_id: c.id, job_id: selectedJob.id, client_id: selectedJob.client_id, created_by: userId });
              }
            }
          } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'create failed';
            setParseWarnings((w) => [...w, `${row.email}: ${msg}`]);
          }
        }
        if (createdIds.length === 0) {
          setErr('No candidates were created — check duplicates or validation errors.');
          setLoading(false); return;
        }
      }
      persistHide();
      onDone({ candidateIds: createdIds, jobId: createApplication && selectedJob ? selectedJob.id : null });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally { setLoading(false); }
  };

  if (!open) return null;

  const renderStep = () => {
    switch (step) {
      case 'decision':
        return (
          <div className="space-y-4">
            <p className="text-sm vscode-text-secondary leading-relaxed">
              Add one or more candidates to your talent pool. Enter details{' '}
              <strong className="vscode-text-primary">manually</strong> or upload a{' '}
              <strong className="vscode-text-primary">CSV / Excel</strong> file, tag the sourcing{' '}
              <strong className="vscode-text-primary">channel</strong> (LinkedIn, Naukri, campus, etc.), and optionally
              link to an open <strong className="vscode-text-primary">job</strong> with{' '}
              <strong className="vscode-text-primary">skill fitment</strong> scoring.
            </p>
            <div className="flex flex-col gap-2">
              <OptionCard
                selected={path === 'balanced'}
                onClick={() => setPath('balanced')}
                title="Balanced"
                badge="Recommended"
                desc="Lifecycle → channel → import → job/fitment. Full walkthrough for new recruiters."
              />
              <OptionCard
                selected={path === 'import_first'}
                onClick={() => setPath('import_first')}
                title="Import-first"
                desc="For bulk lists. Jump straight to file upload — same end state, fewer context steps."
              />
              <OptionCard
                selected={path === 'source_first'}
                onClick={() => setPath('source_first')}
                title="Source-first"
                desc="Emphasise channel hygiene and compliance notes before entering candidate data."
              />
            </div>
            <label className="flex items-center gap-2 text-xs vscode-text-secondary cursor-pointer select-none">
              <input
                type="checkbox"
                className="rounded border-[var(--vscode-border-primary)]"
                checked={hideFuture}
                onChange={(e) => setHideFuture(e.target.checked)}
              />
              Don't show this wizard again (classic form only)
            </label>
          </div>
        );

      case 'lifecycle':
        return (
          <div className="space-y-4">
            {path !== 'balanced' && (
              <p className="text-xs px-3 py-2 rounded border border-[var(--vscode-primary)]/30 bg-[var(--brand-accent-bg,rgba(139,128,249,0.07))] text-[var(--vscode-primary)]">
                {path === 'import_first' ? 'Import-first: you still set sourcing channel — reporting uses both.' : 'Source-first: same steps; tips emphasise channel hygiene.'}
              </p>
            )}
            <p className="text-sm vscode-text-secondary leading-relaxed">{CANDIDATE_LIFECYCLE_HELPER}</p>
            <div className="flex flex-col gap-2">
              {CANDIDATE_LIFECYCLE_PHASES.map((p, i) => (
                <div
                  key={p.key}
                  className="flex items-start gap-3 rounded-lg border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-secondary)] p-3"
                >
                  <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-[var(--vscode-primary)]/15 text-[var(--vscode-primary)] text-[10px] font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-sm font-semibold vscode-text-primary">{p.title}</p>
                    <p className="text-xs vscode-text-secondary mt-0.5 leading-relaxed">{p.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'channel':
        return (
          <div className="space-y-3">
            <p className="text-sm vscode-text-secondary">Where did this candidate come from? Tagging the channel helps track sourcing effectiveness.</p>
            <div className="flex flex-col gap-2">
              {CANDIDATE_INTAKE_CHANNELS.map((c) => (
                <OptionCard
                  key={c.id}
                  selected={intakeId === c.id}
                  onClick={() => setIntakeId(c.id)}
                  title={c.label}
                  desc={c.hint}
                />
              ))}
            </div>
          </div>
        );

      case 'method':
        return (
          <div className="space-y-4">
            <p className="text-sm vscode-text-secondary">How are you adding candidate(s)?</p>
            <div className="flex flex-col gap-2">
              <OptionCard
                selected={method === 'manual'}
                onClick={() => setMethod('manual')}
                title="Manual entry"
                desc="One person at a time. Fill in name, email, skills and job details in the next step."
              />
              <OptionCard
                selected={method === 'bulk'}
                onClick={() => setMethod('bulk')}
                title="CSV / Excel import"
                desc="Upload a spreadsheet (.csv, .xlsx, .xls) with multiple candidates at once."
              />
            </div>
            {method === 'bulk' && (
              <div className="rounded-lg border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-secondary)] p-3 space-y-2">
                <p className="text-xs vscode-text-secondary">{CANDIDATE_IMPORT_COLUMN_HINTS}</p>
                <button
                  type="button"
                  className="btn-secondary text-xs inline-flex items-center gap-1"
                  onClick={() => {
                    const blob = new Blob([candidateImportCsvTemplate()], { type: 'text/csv;charset=utf-8' });
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = 'candidate_import_template.csv';
                    a.click();
                    URL.revokeObjectURL(a.href);
                  }}
                >
                  <Download className="w-3.5 h-3.5" /> Download template
                </button>
              </div>
            )}
          </div>
        );

      case 'intake':
        if (method === 'manual') {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="label text-xs vscode-text-secondary">Full name *</label>
                <input className="input w-full mt-1" value={manual.full_name} onChange={(e) => setManual((m) => ({ ...m, full_name: e.target.value }))} />
              </div>
              <div>
                <label className="label text-xs vscode-text-secondary">Email *</label>
                <input className="input w-full mt-1" type="email" value={manual.email} onChange={(e) => setManual((m) => ({ ...m, email: e.target.value }))} />
              </div>
              <div>
                <label className="label text-xs vscode-text-secondary">Phone</label>
                <input className="input w-full mt-1" value={manual.phone} onChange={(e) => setManual((m) => ({ ...m, phone: e.target.value }))} />
              </div>
              <div className="sm:col-span-2">
                <label className="label text-xs vscode-text-secondary">Skills (comma-separated — used for fitment)</label>
                <input className="input w-full mt-1" placeholder="Java, Spring, AWS" value={manual.skills} onChange={(e) => setManual((m) => ({ ...m, skills: e.target.value }))} />
              </div>
              <div>
                <label className="label text-xs vscode-text-secondary">Current company</label>
                <input className="input w-full mt-1" value={manual.current_company} onChange={(e) => setManual((m) => ({ ...m, current_company: e.target.value }))} />
              </div>
              <div>
                <label className="label text-xs vscode-text-secondary">Current title</label>
                <input className="input w-full mt-1" value={manual.current_title} onChange={(e) => setManual((m) => ({ ...m, current_title: e.target.value }))} />
              </div>
              <div>
                <label className="label text-xs vscode-text-secondary">Location</label>
                <input className="input w-full mt-1" value={manual.location} onChange={(e) => setManual((m) => ({ ...m, location: e.target.value }))} />
              </div>
              <div>
                <label className="label text-xs vscode-text-secondary">LinkedIn URL</label>
                <input className="input w-full mt-1" value={manual.linkedin_url} onChange={(e) => setManual((m) => ({ ...m, linkedin_url: e.target.value }))} />
              </div>
            </div>
          );
        }
        return (
          <div className="space-y-3">
            <input ref={fileRef} type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
            />

            {/* Drop zone — shown when no file loaded yet */}
            {bulkRows.length === 0 ? (
              <div
                role="button"
                tabIndex={0}
                aria-label="Upload file"
                className="relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-secondary)] px-6 py-10 text-center cursor-pointer transition-colors hover:border-[var(--vscode-primary)] hover:bg-[var(--brand-accent-bg,rgba(139,128,249,0.05))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vscode-primary)]"
                onClick={() => fileRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-[var(--vscode-primary)]'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-[var(--vscode-primary)]'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-[var(--vscode-primary)]');
                  void onFile(e.dataTransfer.files?.[0] ?? null);
                }}
              >
                {/* Cloud upload icon */}
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[var(--brand-accent-bg,rgba(139,128,249,0.1))] text-[var(--vscode-primary)]">
                  <Upload className="w-7 h-7" strokeWidth={1.5} />
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-semibold vscode-text-primary">Drag &amp; drop your file here</p>
                  <p className="text-xs vscode-text-secondary">or click to browse from your computer</p>
                </div>

                <div className="flex items-center gap-2 text-[11px] vscode-text-tertiary">
                  <span className="px-2 py-0.5 rounded border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-primary)]">.CSV</span>
                  <span className="px-2 py-0.5 rounded border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-primary)]">.XLSX</span>
                  <span className="px-2 py-0.5 rounded border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-primary)]">.XLS</span>
                  <span>accepted</span>
                </div>

                <button
                  type="button"
                  className="text-xs text-[var(--vscode-primary)] underline underline-offset-2 hover:opacity-80"
                  onClick={(e) => {
                    e.stopPropagation();
                    const blob = new Blob([candidateImportCsvTemplate()], { type: 'text/csv;charset=utf-8' });
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = 'candidate_import_template.csv';
                    a.click();
                    URL.revokeObjectURL(a.href);
                  }}
                >
                  <Download className="w-3 h-3 inline mr-0.5 -mt-0.5" />
                  Download template
                </button>
              </div>
            ) : (
              /* File loaded — preview table + replace button */
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs vscode-text-secondary">
                    <strong className="vscode-text-primary">{bulkRows.length}</strong> row{bulkRows.length !== 1 ? 's' : ''} ready to import
                  </p>
                  <button
                    type="button"
                    className="text-xs text-[var(--vscode-primary)] underline underline-offset-2 hover:opacity-80 inline-flex items-center gap-1"
                    onClick={() => { setBulkRows([]); setParseWarnings([]); fileRef.current?.click(); }}
                  >
                    <Upload className="w-3 h-3" /> Replace file
                  </button>
                </div>
                <div className="overflow-auto rounded-lg border border-[var(--vscode-border-primary)] text-xs" style={{ maxHeight: '180px' }}>
                  <table className="w-full text-left">
                    <thead className="bg-[var(--vscode-bg-secondary)] sticky top-0">
                      <tr>
                        <th className="px-3 py-2 vscode-text-secondary font-medium">Name</th>
                        <th className="px-3 py-2 vscode-text-secondary font-medium">Email</th>
                        <th className="px-3 py-2 vscode-text-secondary font-medium">Skills</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkRows.slice(0, 10).map((r, i) => (
                        <tr key={i} className="border-t border-[var(--vscode-border-divider)]">
                          <td className="px-3 py-2 vscode-text-primary">{r.full_name}</td>
                          <td className="px-3 py-2 vscode-text-secondary">{r.email}</td>
                          <td className="px-3 py-2 vscode-text-tertiary truncate max-w-[140px]">{r.skills.join(', ')}</td>
                        </tr>
                      ))}
                      {bulkRows.length > 10 && (
                        <tr className="border-t border-[var(--vscode-border-divider)]">
                          <td colSpan={3} className="px-3 py-2 vscode-text-tertiary text-center">
                            +{bulkRows.length - 10} more rows…
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {parseWarnings.length > 0 && (
              <ul className="text-xs text-amber-600 dark:text-amber-400 list-disc pl-4 space-y-0.5 max-h-20 overflow-y-auto">
                {parseWarnings.slice(0, 12).map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </div>
        );

      case 'job_fit':
        return (
          <div className="space-y-4">
            <label className="flex items-start gap-2.5 text-sm vscode-text-primary cursor-pointer p-3 rounded-lg border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-secondary)]">
              <input type="checkbox" className="mt-0.5 rounded" checked={createApplication} onChange={(e) => setCreateApplication(e.target.checked)} />
              <span>
                <strong className="font-semibold">Create application</strong> — add this candidate to a job's hiring pipeline.
                <span className="block text-xs vscode-text-secondary mt-0.5">Links the candidate to a job immediately so you can track their progress through stages.</span>
              </span>
            </label>
            {createApplication && (
              <>
                <div>
                  <label className="label text-xs vscode-text-secondary">Job</label>
                  <select className="input w-full mt-1" value={jobId} onChange={(e) => setJobId(e.target.value)}>
                    <option value="">Select job…</option>
                    {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
                  </select>
                </div>
                <label className="flex items-start gap-2.5 text-sm vscode-text-primary cursor-pointer p-3 rounded-lg border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-secondary)]">
                  <input type="checkbox" className="mt-0.5 rounded" checked={computeFitment} onChange={(e) => setComputeFitment(e.target.checked)} />
                  <span>
                    <strong className="font-semibold">Compute skill fitment</strong> (overlap vs job required skills).
                    <span className="block text-xs vscode-text-secondary mt-0.5">Stored on the application. For AI deep gap analysis use Single Optimize from application detail.</span>
                  </span>
                </label>
              </>
            )}
            {!createApplication && (
              <p className="text-xs vscode-text-secondary px-1">
                You can attach roles later from the candidate or applications screens; bulk skill fitment is also available from the candidates list.
              </p>
            )}
          </div>
        );

      case 'review':
        return (
          <div className="space-y-2">
            {[
              ['Path', path === 'balanced' ? 'Balanced (full walkthrough)' : path === 'import_first' ? 'Import-first' : 'Source-first'],
              ['Channel', `${intake?.label} (${intake?.channelLabel})`],
              ['Mode', method === 'manual' ? 'One candidate' : `${bulkRows.length} import row(s)`],
              ...(createApplication && jobId ? [['Job', `${jobs.find((j) => j.id === jobId)?.title ?? jobId}${computeFitment ? ' · with skill fitment' : ''}`]] : []),
            ].map(([label, val]) => (
              <div key={label} className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-secondary)]">
                <span className="text-xs vscode-text-secondary w-20 shrink-0 pt-px">{label}</span>
                <span className="text-sm font-medium vscode-text-primary">{val}</span>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      onClose={onDismiss}
      title="Candidate Intake"
      subtitle="Step-by-step sourcing and import wizard"
      icon={<UserPlus className="w-4 h-4" />}
      size="lg"
    >
      <div className="flex flex-col gap-4" style={{ height: 'min(500px, calc(90vh - 130px))' }}>

        {/* Horizontal step tracker */}
        <div className="shrink-0">
          <HorizontalStepper
            steps={STEPS.map((s) => ({ id: s, label: STEP_LABELS[s] }))}
            currentIdx={stepIdx}
          />
        </div>

        {/* Body */}
        <div className="min-h-0 overflow-y-auto flex-1 pr-0.5">
          {renderStep()}
        </div>

        {/* Error */}
        {err && (
          <p className="text-xs text-red-600 dark:text-red-400 shrink-0 px-1">{err}</p>
        )}

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-[var(--vscode-border-primary)] shrink-0">
          <button type="button" className="text-xs vscode-text-tertiary hover:vscode-text-secondary underline" onClick={skip}>
            Skip wizard — classic form
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary text-sm inline-flex items-center gap-1"
              disabled={loading}
              onClick={goBack}
            >
              <ChevronLeft className="w-4 h-4" />
              {stepIdx === 0 ? 'Back to list' : 'Back'}
            </button>
            {step === 'review' ? (
              <button
                type="button"
                className="btn-primary text-sm inline-flex items-center gap-1.5"
                disabled={loading}
                onClick={() => void runSubmit()}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Save candidate{method === 'bulk' ? 's' : ''}
              </button>
            ) : (
              <button type="button" className="btn-primary text-sm inline-flex items-center gap-1.5" onClick={goNext}>
                Next <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

      </div>
    </Modal>
  );
}
